import React, { useState } from "react";
import {
  Box,
  Modal,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";
import HistoryIcon from "@mui/icons-material/History";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import "../styles/PendingDataPopup.scss";

const PendingDataPopup = ({ isOpen, onClose, data }) => {
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();

  let userName = "";
  const storedUser = Cookies.get("user");
  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      userName =
        parsedUser && typeof parsedUser.name === "string" ? parsedUser.name : "";
    } catch (e) {
      console.error("Error parsing user cookie:", e);
    }
  }

  if (!data) return null;

  const { pending, new_unlocked } = data;

  const activeRecallCount =
    (pending?.active_recall?.length || 0) +
    (new_unlocked?.active_recall?.length || 0);

  const adaptiveLearningCount =
    (pending?.adaptive_learning?.length || 0) +
    (new_unlocked?.adaptive_learning?.length || 0);

  const totalPending =
    (pending?.active_recall?.length || 0) +
    (pending?.adaptive_learning?.length || 0);

  const totalUnlocked =
    (new_unlocked?.active_recall?.length || 0) +
    (new_unlocked?.adaptive_learning?.length || 0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleItemClick = (item) => {
    let targetPath = item.path;
    if (item.type === "active_recall" && !targetPath.startsWith("/")) {
      targetPath = `/courses/${targetPath}`;
    }
    navigate(targetPath);
    onClose();
  };

  const renderItemList = (items, type, status) => {
    if (!items || items.length === 0) return null;

    const statusClass = status === "pending" ? "pending" : "unlocked";

    return (
      <List className="pending-popup-list">
        {items.map((item, index) => (
          <Paper
            key={index}
            elevation={0}
            className={`pending-popup-item-card ${statusClass}-item`}
            onClick={() => handleItemClick(item)}
            style={{ cursor: "pointer" }}
          >
            <ListItem className="pending-popup-item-inner">
              <ListItemIcon className="pending-popup-item-icon-wrapper">
                <Box
                  className={`pending-popup-item-icon-box ${statusClass}-icon`}
                >
                  {type === "active_recall" ? (
                    <AssignmentIcon fontSize="small" />
                  ) : (
                    <SchoolIcon fontSize="small" />
                  )}
                </Box>
              </ListItemIcon>
              <ListItemText
                className="pending-popup-item-text"
                primary={
                  item.name ||
                  item.context?.subtopic?.name ||
                  item.context?.topic?.name ||
                  (type === "active_recall"
                    ? "Active Recall"
                    : "Adaptive Learning")
                }
              />
              <ChevronRightIcon className="pending-popup-item-arrow" />
            </ListItem>
          </Paper>
        ))}
      </List>
    );
  };

  const renderTabContent = (categoryKey) => {
    const pendingItems = pending?.[categoryKey] || [];
    const unlockedItems = new_unlocked?.[categoryKey] || [];
    const categoryLabel =
      categoryKey === "active_recall" ? "Active Recalls" : "Adaptive Learnings";

    return (
      <Box className="pending-popup-tab-pane">
        <Accordion className="pending-popup-accordion" elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon className="accordion-expand-icon" />}
            className="pending-popup-accordion-summary pending-header"
          >
            <HistoryIcon className="pending-popup-section-icon" />
            <div className="pending-popup-section-title">Pending Tasks</div>
            <Box className="pending-popup-section-badge">
              {pendingItems.length}
            </Box>
          </AccordionSummary>
          <AccordionDetails className="pending-popup-accordion-details">
            {pendingItems.length > 0 ? (
              renderItemList(pendingItems, categoryKey, "pending")
            ) : (
              <Box className="pending-popup-empty-item">
                <div className="pending-popup-empty-text">
                  No pending {categoryLabel.toLowerCase()}
                </div>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion className="pending-popup-accordion" elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon className="accordion-expand-icon" />}
            className="pending-popup-accordion-summary unlocked-header"
          >
            <LockOpenIcon className="pending-popup-section-icon" />
            <div className="pending-popup-section-title">Newly Unlocked</div>
            <Box className="pending-popup-section-badge">
              {unlockedItems.length}
            </Box>
          </AccordionSummary>
          <AccordionDetails className="pending-popup-accordion-details">
            {unlockedItems.length > 0 ? (
              renderItemList(unlockedItems, categoryKey, "unlocked")
            ) : (
              <Box className="pending-popup-empty-item">
                <div className="pending-popup-empty-text">
                  No newly unlocked {categoryLabel.toLowerCase()}
                </div>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="pending-data-modal-title"
      className="common-popup-overlay pending-popup-overlay"
      disableAutoFocus
    >
      <Box className="pending-popup-container">
        <Box className="pending-popup-header">
          <Box className="pending-popup-title-box">
            <div id="pending-data-modal-title" className="pending-popup-title">
              Welcome back {userName} !
            </div>
            <div className="pending-popup-subtitle">
              {totalPending > 0 || totalUnlocked > 0
                ? `You have ${totalPending} pending tasks and ${totalUnlocked} newly unlocked items today.`
                : "No pending tasks or newly unlocked items for today. Great job!"}
            </div>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            className="pending-popup-close-btn"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className="pending-popup-tabs-container">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            className="pending-popup-tabs"
          >
            <Tab
              label={`Active Recall ${activeRecallCount > 0 ? `(${activeRecallCount})` : ""}`}
            />
            <Tab
              label={`Adaptive Learning ${adaptiveLearningCount > 0 ? `(${adaptiveLearningCount})` : ""}`}
            />
          </Tabs>
        </Box>

        <Box className="pending-popup-content custom-scrollbar">
          {tabValue === 0
            ? renderTabContent("active_recall")
            : renderTabContent("adaptive_learning")}
        </Box>
      </Box>
    </Modal>
  );
};

export default PendingDataPopup;
